import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMsal } from "@azure/msal-react";
import { getAPICall } from "../../../config/apiCalls";

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const LEFT_W = 160;
const RIGHT_W = 192;
const ROW_H = 68;
const HOURS_PER_WORKDAY = 8;

const addDays = (d, n) => new Date(d.getTime() + n * MS_PER_DAY);
const daysBetween = (a, b) => Math.round((b.getTime() - a.getTime()) / MS_PER_DAY);

// Normalize a name for fuzzy matching against the employees table.
// Handles "Last, First Middle" → "first middle last" and "First Last" → "first last".
const normalizeName = (n) => {
	if (!n) return "";
	const s = n.trim();
	if (s.includes(",")) {
		const comma = s.indexOf(",");
		const last = s.slice(0, comma).trim();
		const rest = s.slice(comma + 1).trim();
		return `${rest} ${last}`.toLowerCase();
	}
	return s.toLowerCase();
};
const fmtDate = (d) =>
	d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

// Parse date strings as local dates — "2026-04-14" would be treated as UTC midnight
// by `new Date()`, shifting it one day behind in US timezones.
const parseLocalDate = (value) => {
	if (!value) return null;
	// Strip time portion (handles "YYYY-MM-DD", "YYYY-MM-DDTHH:MM:SS", "YYYY-MM-DD HH:MM:SS")
	const datePart = String(value).split(/[T ]/)[0];
	const parts = datePart.split("-");
	if (parts.length === 3) {
		const d = new Date(+parts[0], +parts[1] - 1, +parts[2]);
		return isNaN(d.getTime()) ? null : d;
	}
	const d = new Date(String(value));
	return isNaN(d.getTime()) ? null : d;
};

// ─── TimeEntryChip ─────────────────────────────────────────────────────────────

function TimeEntryChip({ entry, left, trackW }) {
	const height = 30;
	const clampedLeft = Math.max(2, Math.min(trackW - 28, left));
	const isBillable = entry.billable;
	return (
		<Tooltip
			arrow
			placement="top"
			title={
				<div>
					<div style={{ fontWeight: 700, fontSize: 12 }}>{fmtDate(entry.date)}</div>
					<div style={{ fontSize: 12 }}>
						{entry.hours}h{!isBillable ? " (non-billable)" : ""}
					</div>
					{entry.memo && (
						<div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>{entry.memo}</div>
					)}
				</div>
			}
		>
			<div
				style={{
					position: "absolute",
					left: clampedLeft,
					top: "50%",
					transform: "translateY(-50%)",
					width: 26,
					height,
					background: isBillable ? "#b9f0c0" : "#fee2e2",
					border: `1.5px solid ${isBillable ? "#4ea861" : "#ef4444"}`,
					borderRadius: 6,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: 11,
					fontWeight: 600,
					color: isBillable ? "#1a3d23" : "#7f1d1d",
					cursor: "default",
					zIndex: 2,
					userSelect: "none",
				}}
			>
				{entry.hours}
			</div>
		</Tooltip>
	);
}

// ─── BudgetBar ─────────────────────────────────────────────────────────────────

function BudgetBar({ hours, leftPx, widthPx }) {
	if (widthPx <= 12) return null;
	return (
		<Tooltip arrow placement="top" title={`Planned: ${hours}h this week`}>
			<div
				style={{
					position: "absolute",
					left: leftPx + 6,
					width: widthPx - 12,
					height: 18,
					top: "50%",
					transform: "translateY(-50%)",
					background: "#bcd8ff",
					border: "1px solid #5b8dd6",
					borderRadius: 6,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: 11,
					fontWeight: 600,
					color: "#19345e",
					overflow: "hidden",
					zIndex: 1,
				}}
			>
				{hours}
			</div>
		</Tooltip>
	);
}

// ─── mergeConsecutivePeriods ──────────────────────────────────────────────────
// Merges any periods whose dates are consecutive or overlapping into one span.

function mergeConsecutivePeriods(periods) {
	if (!periods.length) return [];
	const sorted = [...periods].sort((a, b) => a.start.getTime() - b.start.getTime());
	const merged = [{ start: sorted[0].start, end: sorted[0].end }];
	for (let i = 1; i < sorted.length; i++) {
		const last = merged[merged.length - 1];
		const cur = sorted[i];
		// Consecutive means cur.start is at most one day after last.end
		if (cur.start.getTime() <= addDays(last.end, 1).getTime()) {
			if (cur.end.getTime() > last.end.getTime()) last.end = cur.end;
		} else {
			merged.push({ start: cur.start, end: cur.end });
		}
	}
	return merged;
}

// ─── mergeConsecutiveHolidays ─────────────────────────────────────────────────
// Merges consecutive or overlapping holidays into single spans, combining names.

function mergeConsecutiveHolidays(hols) {
	if (!hols.length) return [];
	const sorted = [...hols].sort((a, b) => a.start.getTime() - b.start.getTime());
	const merged = [{ start: sorted[0].start, end: sorted[0].end, names: [sorted[0].name] }];
	for (let i = 1; i < sorted.length; i++) {
		const last = merged[merged.length - 1];
		const cur = sorted[i];
		if (cur.start.getTime() <= addDays(last.end, 1).getTime()) {
			if (cur.end.getTime() > last.end.getTime()) last.end = cur.end;
			if (!last.names.includes(cur.name)) last.names.push(cur.name);
		} else {
			merged.push({ start: cur.start, end: cur.end, names: [cur.name] });
		}
	}
	return merged;
}

// ─── TimeOffBar ────────────────────────────────────────────────────────────────
// Renders a single box for a contiguous off period.

function TimeOffBar({ startDate, endDate, effectiveStart, dayToPx, pxPerDay, trackW }) {
	const startDay = daysBetween(effectiveStart, startDate);
	// endDate is inclusive, so the box spans (endDay - startDay + 1) days
	const endDay = daysBetween(effectiveStart, endDate);
	const leftPx = dayToPx(startDay);
	const rightPx = dayToPx(endDay) + pxPerDay;

	if (leftPx > trackW || rightPx < 0) return null;

	const clampedLeft = Math.max(0, leftPx);
	const clampedRight = Math.min(trackW, rightPx);
	const clampedWidth = clampedRight - clampedLeft;
	if (clampedWidth <= 0) return null;

	const isSingleDay = startDate.getTime() === endDate.getTime();
	const label = isSingleDay
		? `Off: ${fmtDate(startDate)}`
		: `Off: ${fmtDate(startDate)} – ${fmtDate(endDate)}`;

	return (
		<Tooltip arrow placement="top" title={label}>
			<div
				style={{
					position: "absolute",
					left: clampedLeft,
					width: clampedWidth,
					top: 0,
					bottom: 0,
					background: "rgba(124, 58, 237, 0.08)",
					borderLeft: leftPx >= 0 ? "1.5px solid rgba(124, 58, 237, 0.40)" : "none",
					borderRight: rightPx <= trackW ? "1.5px solid rgba(124, 58, 237, 0.40)" : "none",
					borderTop: "1px solid rgba(124, 58, 237, 0.18)",
					borderBottom: "1px solid rgba(124, 58, 237, 0.18)",
					zIndex: 1,
					cursor: "default",
					pointerEvents: "all",
					boxSizing: "border-box",
				}}
			/>
		</Tooltip>
	);
}

// ─── Scrollbar ─────────────────────────────────────────────────────────────────

function TimelineScrollbar({ value, max, thumbFraction, onChange, disabled }) {
	const trackRef = useRef(null);

	const onPointerDown = (e) => {
		if (disabled) return;
		const track = trackRef.current;
		if (!track) return;
		const rect = track.getBoundingClientRect();
		const thumbW = rect.width * thumbFraction;
		const usable = rect.width - thumbW;
		const isThumb = e.target?.dataset?.thumb === "1";
		let sv = value;
		const startX = e.clientX;
		if (!isThumb && usable > 0) {
			const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left - thumbW / 2) / usable));
			sv = ratio * max;
			onChange(sv);
		}
		const onMove = (ev) => {
			if (usable <= 0) return;
			onChange(Math.max(0, Math.min(max, sv + ((ev.clientX - startX) / usable) * max)));
		};
		const onUp = () => {
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
		};
		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
		e.preventDefault();
	};

	const ratio = max > 0 ? Math.min(1, value / max) : 0;
	const thumbPct = Math.min(100, thumbFraction * 100);
	const leftPct = ratio * (100 - thumbPct);

	return (
		<div
			ref={trackRef}
			onPointerDown={onPointerDown}
			style={{
				position: "relative",
				height: 16,
				background: "#eef0f3",
				border: "1px solid #d8dce3",
				borderRadius: 8,
				cursor: disabled ? "default" : "pointer",
				opacity: disabled ? 0.4 : 1,
				userSelect: "none",
				touchAction: "none",
			}}
		>
			<div
				data-thumb="1"
				style={{
					position: "absolute",
					top: 2,
					bottom: 2,
					left: `${leftPct}%`,
					width: `${thumbPct}%`,
					background: "#fff",
					border: "1px solid #b6bcc7",
					borderRadius: 6,
					cursor: disabled ? "default" : "grab",
					boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
				}}
			/>
		</div>
	);
}

// ─── LegendSwatch ──────────────────────────────────────────────────────────────

function LegendSwatch({ color, border, label, info }) {
	return (
		<span
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 5,
				fontSize: 12,
				color: "#7a8190",
			}}
		>
			<span
				style={{
					display: "inline-block",
					width: 16,
					height: 12,
					background: color,
					border: `1px solid ${border}`,
					borderRadius: 3,
					flexShrink: 0,
				}}
			/>
			{label}
			{info && (
				<Tooltip arrow placement="top" title={info}>
					<HelpOutlineIcon style={{ fontSize: 16, color: "#b6bcc7", cursor: "default" }} />
				</Tooltip>
			)}
		</span>
	);
}

// ─── ScheduleDurationDisplay ────────────────────────────────────────────────────
// Duration is calculated, never editable — a single small line tucked right under
// its start date field, not a full field of its own.

function ScheduleDurationDisplay({ label, weeks, invalidRange }) {
	return (
		<Typography
			variant="caption"
			color={invalidRange ? "error" : "text.secondary"}
			sx={{ mt: -1, mb: 0.5 }}
		>
			{invalidRange
				? "End date must be after start date."
				: `${label}: ${weeks != null ? `${weeks} week${weeks === 1 ? "" : "s"}` : "—"}`}
		</Typography>
	);
}

// ─── ScheduleFieldsEditor ──────────────────────────────────────────────────────
// Display row + edit dialog for the project's start/duration/end trio. When the
// phase is on hold, the on-hold trio is shown inline in the same row and edited
// in the same dialog via the same Edit/Save action.

function ScheduleFieldTrio({ fieldLabels, parsedStart, parsedEnd, durationWeeks, todayDate, highlightPastEnd }) {
	return (
		<>
			<Stack>
				<Typography variant="caption" color="text.secondary">{fieldLabels.start}</Typography>
				<Typography variant="body2" fontWeight={600}>
					{parsedStart ? fmtDate(parsedStart) : <em style={{ color: "#b6bcc7" }}>Not set</em>}
				</Typography>
			</Stack>
			<Stack>
				<Typography variant="caption" color="text.secondary">{fieldLabels.duration}</Typography>
				<Typography variant="body2" fontWeight={600}>
					{durationWeeks != null ? durationWeeks : <em style={{ color: "#b6bcc7" }}>Not set</em>}
				</Typography>
			</Stack>
			<Stack>
				<Typography variant="caption" color="text.secondary">{fieldLabels.end}</Typography>
				<Typography
					variant="body2"
					fontWeight={600}
					color={highlightPastEnd && parsedEnd && todayDate && parsedEnd < todayDate ? "error" : "text.primary"}
				>
					{parsedEnd ? fmtDate(parsedEnd) : <em style={{ color: "#b6bcc7" }}>Not set</em>}
				</Typography>
			</Stack>
		</>
	);
}

// Duration is never an input — it's always calculated from whichever start/end
// pair is currently in the draft, for both the main and on-hold sections.
const computeWeeks = (startStr, endStr) => {
	const s = parseLocalDate(startStr);
	const e = parseLocalDate(endStr);
	if (!s || !e) return null;
	return Math.round(daysBetween(s, e) / 7);
};
const isInvalidRange = (startStr, endStr) => {
	const s = parseLocalDate(startStr);
	const e = parseLocalDate(endStr);
	return !!(s && e && e <= s);
};

// ─── useScheduleFormState ───────────────────────────────────────────────────────
// All the draft state, validation, and derived values for a start/end (+ optional
// on-hold) form. Shared by ScheduleFieldsEditor (Schedule tab) and the combined
// Status + Schedule dialog so the two never drift apart on this logic.
// onHold param shape: { rawStart, rawEndDate } | null

export function useScheduleFormState(rawStart, rawEndDate, onHold, { datesRequired = true } = {}) {
	const [draftStart, setDraftStart] = useState("");
	const [draftEndDate, setDraftEndDate] = useState("");
	const [startError, setStartError] = useState("");
	const [endError, setEndError] = useState("");

	const [onHoldEnabled, setOnHoldEnabled] = useState(false);
	const [onHoldHadExisting, setOnHoldHadExisting] = useState(false);
	const [draftOnHoldStart, setDraftOnHoldStart] = useState("");
	const [draftOnHoldEndDate, setDraftOnHoldEndDate] = useState("");
	const [onHoldStartError, setOnHoldStartError] = useState("");
	const [onHoldEndError, setOnHoldEndError] = useState("");

	const reset = () => {
		setDraftStart(rawStart || "");
		setDraftEndDate(rawEndDate || "");
		setStartError("");
		setEndError("");
		if (onHold) {
			const hadExisting = !!(onHold.rawStart || onHold.rawEndDate);
			setOnHoldHadExisting(hadExisting);
			setOnHoldEnabled(hadExisting);
			setDraftOnHoldStart(onHold.rawStart || "");
			setDraftOnHoldEndDate(onHold.rawEndDate || "");
			setOnHoldStartError("");
			setOnHoldEndError("");
		}
	};

	const validateStart = (val) => {
		if (datesRequired && !val) { setStartError("Start date is required."); return false; }
		setStartError(""); return true;
	};
	const validateEnd = (val) => {
		if (datesRequired && !val) { setEndError("End date is required."); return false; }
		setEndError(""); return true;
	};
	const validateOnHoldStart = (val) => {
		if (!val) { setOnHoldStartError("On hold start date is required."); return false; }
		setOnHoldStartError(""); return true;
	};
	const validateOnHoldEnd = (val) => {
		if (!val) { setOnHoldEndError("On hold end date is required."); return false; }
		setOnHoldEndError(""); return true;
	};

	const draftInvalidRange = !!(draftStart && draftEndDate && isInvalidRange(draftStart, draftEndDate));
	const draftDurationWeeks = draftInvalidRange ? null : computeWeeks(draftStart, draftEndDate);

	const onHoldInvalidRange = onHoldEnabled && !!(draftOnHoldStart && draftOnHoldEndDate && isInvalidRange(draftOnHoldStart, draftOnHoldEndDate));
	const draftOnHoldDurationWeeks = onHoldEnabled && !onHoldInvalidRange ? computeWeeks(draftOnHoldStart, draftOnHoldEndDate) : null;

	// On hold period must fall within the project's own start/end — it can start on
	// the same day as the project, but not before; it can end on the same day as the
	// project, but not after.
	const onHoldStartBeforeProjectStart = onHoldEnabled && !!(draftOnHoldStart && draftStart
		&& parseLocalDate(draftOnHoldStart) < parseLocalDate(draftStart));
	const onHoldEndAfterProjectEnd = onHoldEnabled && !!(draftOnHoldEndDate && draftEndDate
		&& parseLocalDate(draftOnHoldEndDate) > parseLocalDate(draftEndDate));

	const onHoldStartErrorText = onHoldStartError
		|| (onHoldStartBeforeProjectStart ? "Can't be before the project start date." : "");
	const onHoldEndErrorText = onHoldEndError
		|| (onHoldEndAfterProjectEnd ? "Can't be after the project end date." : "");

	const validateAll = () => {
		const ok = [validateStart(draftStart), validateEnd(draftEndDate)];
		if (onHoldEnabled) ok.push(validateOnHoldStart(draftOnHoldStart), validateOnHoldEnd(draftOnHoldEndDate));
		if (ok.some((v) => !v)) return false;
		if (draftInvalidRange) { setEndError("End date must be after start date."); return false; }
		if (onHoldEnabled) {
			if (onHoldInvalidRange) { setOnHoldEndError("End date must be after start date."); return false; }
			if (onHoldStartBeforeProjectStart || onHoldEndAfterProjectEnd) return false;
		}
		return true;
	};

	const allFilled = (!datesRequired || !!(draftStart && draftEndDate)) && !draftInvalidRange
		&& (!onHold || !onHoldEnabled || (
			!!(draftOnHoldStart && draftOnHoldEndDate)
			&& !onHoldInvalidRange
			&& !onHoldStartBeforeProjectStart
			&& !onHoldEndAfterProjectEnd
		));

	return {
		draftStart, setDraftStart, draftEndDate, setDraftEndDate, startError, endError,
		validateStart, validateEnd,
		onHoldEnabled, setOnHoldEnabled, onHoldHadExisting,
		draftOnHoldStart, setDraftOnHoldStart, draftOnHoldEndDate, setDraftOnHoldEndDate,
		onHoldStartErrorText, onHoldEndErrorText, validateOnHoldStart, validateOnHoldEnd,
		draftDurationWeeks, draftInvalidRange, draftOnHoldDurationWeeks, onHoldInvalidRange,
		reset, validateAll, allFilled,
	};
}

// ─── ScheduleFormFields ──────────────────────────────────────────────────────────
// Presentational only — Start/Duration/[on-hold toggle+fields]/End, driven entirely
// by a useScheduleFormState() result. No Dialog wrapper, no Save/Cancel — the
// caller supplies those so each dialog can have its own save flow around the same
// fields.

export function ScheduleFormFields({ fieldLabels, onHold, state }) {
	return (
		<>
			<TextField
				label={fieldLabels.start}
				type="date"
				size="small"
				value={state.draftStart}
				onChange={(e) => state.setDraftStart(e.target.value)}
				onBlur={(e) => state.validateStart(e.target.value)}
				InputLabelProps={{ shrink: true }}
				error={!!state.startError}
				helperText={state.startError}
				fullWidth
			/>
			<ScheduleDurationDisplay
				label={fieldLabels.duration}
				weeks={state.draftDurationWeeks}
				invalidRange={state.draftInvalidRange}
			/>

			{onHold && (
				<>
					<Divider sx={{ my: 0.5 }} />
					<Stack direction="row" alignItems="center" gap={1}>
						<Checkbox
							checked={state.onHoldEnabled}
							onChange={(e) => state.setOnHoldEnabled(e.target.checked)}
							sx={{ p: 0.5 }}
						/>
						<Typography variant="body2">Add on hold period</Typography>
					</Stack>
					{state.onHoldEnabled && (
						<>
							<TextField
								label={onHold.fieldLabels.start}
								type="date"
								size="small"
								value={state.draftOnHoldStart}
								onChange={(e) => state.setDraftOnHoldStart(e.target.value)}
								onBlur={(e) => state.validateOnHoldStart(e.target.value)}
								InputLabelProps={{ shrink: true }}
								inputProps={{
									min: state.draftStart || undefined,
									max: state.draftOnHoldEndDate || state.draftEndDate || undefined,
								}}
								error={!!state.onHoldStartErrorText}
								helperText={state.onHoldStartErrorText}
								fullWidth
							/>
							<ScheduleDurationDisplay
								label={onHold.fieldLabels.duration}
								weeks={state.draftOnHoldDurationWeeks}
								invalidRange={state.onHoldInvalidRange}
							/>
							<TextField
								label={onHold.fieldLabels.end}
								type="date"
								size="small"
								value={state.draftOnHoldEndDate}
								onChange={(e) => state.setDraftOnHoldEndDate(e.target.value)}
								onBlur={(e) => state.validateOnHoldEnd(e.target.value)}
								InputLabelProps={{ shrink: true }}
								inputProps={{
									min: state.draftOnHoldStart || state.draftStart || undefined,
									max: state.draftEndDate || undefined,
								}}
								error={!!state.onHoldEndErrorText}
								helperText={state.onHoldEndErrorText}
								fullWidth
							/>
						</>
					)}
					<Divider sx={{ my: 0.5 }} />
				</>
			)}

			<TextField
				label={fieldLabels.end}
				type="date"
				size="small"
				value={state.draftEndDate}
				onChange={(e) => state.setDraftEndDate(e.target.value)}
				onBlur={(e) => state.validateEnd(e.target.value)}
				InputLabelProps={{ shrink: true }}
				error={!!state.endError}
				helperText={state.endError}
				fullWidth
			/>
		</>
	);
}

function ScheduleFieldsEditor({
	fieldLabels,
	rawStart,
	rawEndDate,
	parsedStart,
	parsedEnd,
	durationWeeks,
	todayDate,
	highlightPastEnd = false,
	dialogTitle,
	onSave,
	onHold = null, // { fieldLabels, rawStart, rawEndDate, parsedStart, parsedEnd, durationWeeks, onSave } | null
}) {
	const [isEditing, setIsEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const formState = useScheduleFormState(rawStart, rawEndDate, onHold);

	const handleEditClick = () => {
		formState.reset();
		setSaveError("");
		setIsEditing(true);
	};

	const handleCancel = () => {
		setIsEditing(false);
		setSaveError("");
	};

	const handleSave = async () => {
		if (!onSave) return;
		if (!formState.validateAll()) return;

		setSaving(true);
		setSaveError("");
		try {
			await onSave(formState.draftStart, formState.draftDurationWeeks, formState.draftEndDate);
			if (onHold) {
				if (formState.onHoldEnabled) {
					await onHold.onSave(formState.draftOnHoldStart, formState.draftOnHoldDurationWeeks, formState.draftOnHoldEndDate);
				} else if (formState.onHoldHadExisting) {
					// Unchecked on a phase that had on-hold dates — clear them rather than
					// leaving stale dates that could silently reactivate on a future hold.
					await onHold.onSave(null, null, null);
				}
			}
			setIsEditing(false);
		} catch (err) {
			setSaveError(err?.response?.data?.error || err?.message || "Save failed.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<Stack direction="row" flexWrap="wrap" gap={2} alignItems="center" sx={{ mb: 1.5 }}>
				<ScheduleFieldTrio
					fieldLabels={fieldLabels}
					parsedStart={parsedStart}
					parsedEnd={parsedEnd}
					durationWeeks={durationWeeks}
					todayDate={todayDate}
					highlightPastEnd={highlightPastEnd}
				/>
				{onHold && (onHold.parsedStart || onHold.parsedEnd) && (
					<ScheduleFieldTrio
						fieldLabels={onHold.fieldLabels}
						parsedStart={onHold.parsedStart}
						parsedEnd={onHold.parsedEnd}
						durationWeeks={onHold.durationWeeks}
					/>
				)}
				{onSave && (
					<Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={handleEditClick}>
						Edit
					</Button>
				)}
			</Stack>

			<Dialog open={isEditing} onClose={handleCancel} maxWidth="xs" fullWidth>
				<DialogTitle className="scheduleEditDialogTitle">{dialogTitle}</DialogTitle>
				<DialogContent className="scheduleEditDialogContent">
					<ScheduleFormFields fieldLabels={fieldLabels} onHold={onHold} state={formState} />

					{saveError && (
						<Typography variant="caption" color="error">{saveError}</Typography>
					)}
					<div className="dialogButtons">
						<Button variant="outlined" color="primary" onClick={handleCancel} disabled={saving}>
							Cancel
						</Button>
						<Tooltip
							arrow
							placement="top"
							title={!formState.allFilled ? "Start date and end date are required." : ""}
						>
							<span>
								<Button
									variant="contained"
									color="primary"
									disableElevation
									onClick={handleSave}
									disabled={saving || !formState.allFilled}
									startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
								>
									{saving ? "Saving…" : "Save"}
								</Button>
							</span>
						</Tooltip>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}

// ─── ScheduleTab ───────────────────────────────────────────────────────────────

export default function ScheduleTab({
	atAGlanceSection,
	budgetsSection,
	timeEntriesSection,
	sectionStatus,
	phaseRecord,
	timeOffByName = new Map(),
	holidays = [],
	holidaysLoaded = true,
	onSaveSchedule = null,
	onHoldSection = null,
	onSaveOnHoldSchedule = null,
}) {
	const { instance, accounts } = useMsal();

	const [empHoursMap, setEmpHoursMap] = useState(new Map());

	useEffect(() => {
		const fetchEmployees = async () => {
			try {
				const res = await getAPICall(instance, accounts[0], "/api/workload/employees");
				const map = new Map();
				(res.data || []).forEach((e) => {
					map.set(normalizeName(e.name), {
						hoursPerDay:    e.hours_per_week ? Number(e.hours_per_week) / 5 : 8,
						hoursPerWeek:   e.hours_per_week ?? null,
						employmentType: e.employment_type ?? null,
					});
				});
				setEmpHoursMap(map);
			} catch (err) {
				console.error("[ScheduleTab] employee hours fetch failed", err);
			}
		};
		fetchEmployees();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [view, setView] = useState("weeks");
	const [scroll, setScroll] = useState(0);
	const chartRef = useRef(null);
	const trackRef = useRef(null);
	const [trackW, setTrackW] = useState(600);
	const scrollInitialized = useRef(false);
	const loadingEverStarted = useRef(false);

	// Re-measure after every render — catches sidebar collapse (parent re-render) and initial mount
	useLayoutEffect(() => {
		if (!chartRef.current) return;
		const w = Math.max(0, chartRef.current.clientWidth - LEFT_W - RIGHT_W);
		setTrackW((prev) => (prev === w ? prev : w));
	});

	// ResizeObserver handles window/external resizes that don't come from React re-renders
	useEffect(() => {
		const update = () => {
			if (chartRef.current) {
				setTrackW(Math.max(0, chartRef.current.clientWidth - LEFT_W - RIGHT_W));
			}
		};
		const ro = new ResizeObserver(update);
		if (chartRef.current) ro.observe(chartRef.current);
		return () => ro.disconnect();
	}, []);

	const isLoading =
		sectionStatus?.at_a_glance?.loading ||
		sectionStatus?.budgets?.loading ||
		sectionStatus?.time_entries?.loading ||
		!holidaysLoaded;

	// ── Build people + timeline bounds ─────────────────────────────────────────

	const scheduleData = useMemo(() => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const durInfo = atAGlanceSection?.estimated_project_duration || {};
		const projectStart = parseLocalDate(
			durInfo.start_date ||
			phaseRecord?.["CF-Start Date"]
		);
		const projectEnd = parseLocalDate(
			durInfo.estimated_end_date ||
			durInfo.end_date ||
			phaseRecord?.["CF-End Date"]
		);
		// Duration is never stored/read as its own value — it's always calculated from
		// start and end so it can never disagree with them.
		const durationWeeks = projectStart && projectEnd
			? Math.round(daysBetween(projectStart, projectEnd) / 7)
			: null;

		const budgetItems = budgetsSection?.service_line_items || [];
		const allEntries = timeEntriesSection?.entries || [];

		// Budget items with no resource_id must be matched by name.
		// For those, time entry keys should also use the name so they land in the same group.
		const budgetIdLessNames = new Set(
			budgetItems.filter((i) => !i.resource_id && i.resource).map((i) => i.resource)
		);
		const budgetNameToId = new Map();
		budgetItems.forEach((i) => { if (i.resource_id && i.resource) budgetNameToId.set(i.resource, i.resource_id); });

		const budgetKey = (i) => i.resource_id || i.resource || "";
		const entryKey = (e) => {
			// If the budget for this person has no resource_id, match by name
			if (e.employee && budgetIdLessNames.has(e.employee)) return e.employee;
			return e.resource_id || budgetNameToId.get(e.employee) || e.employee || "";
		};

		const keySet = new Set([
			...budgetItems.map(budgetKey).filter(Boolean),
			...allEntries.map(entryKey).filter(Boolean),
		]);

		// Canonical key → display name: prefer employee name from entries (actual person), fall back to budget resource name
		const keyToName = new Map();
		budgetItems.forEach((i) => { if (budgetKey(i)) keyToName.set(budgetKey(i), i.resource); });
		allEntries.forEach((e) => { const k = entryKey(e); if (k && e.employee) keyToName.set(k, e.employee); });

		const people = [...keySet].map((key) => {
			const name = keyToName.get(key) || key;
			const personBudgetItems = budgetItems.filter((i) => budgetKey(i) === key);
			const inBudget = personBudgetItems.length > 0;
			const budget = inBudget
				? Math.round(personBudgetItems.reduce((sum, i) => sum + (i.hours ?? 0), 0) * 100) / 100
				: null;
			const entries = allEntries
				.filter((e) => entryKey(e) === key)
				.map((e) => {
					const d = parseLocalDate(e.date);
					return d ? { date: d, hours: Math.round((e.hours || 0) * 100) / 100, memo: e.memo || "", billable: !!e.billable } : null;
				})
				.filter(Boolean);
			const spent =
				Math.round(
					entries.filter((e) => e.billable).reduce((s, e) => s + e.hours, 0) * 100
				) / 100;
			const remaining =
				budget !== null ? Math.round((budget - spent) * 100) / 100 : null;
			return { name, budget, spent, remaining, inBudget, entries };
		});

		people.sort((a, b) => {
			if (a.inBudget !== b.inBudget) return a.inBudget ? -1 : 1;
			return a.name.localeCompare(b.name);
		});

		const allDates = people.flatMap((p) => p.entries.map((e) => e.date.getTime()));
		const noAnchor = !projectStart && allDates.length === 0 && budgetItems.length > 0;
		const effectiveStart = projectStart
			|| (allDates.length ? new Date(Math.min(...allDates)) : noAnchor ? addDays(today, -28) : today);
		const latestEntry = allDates.length ? new Date(Math.max(...allDates)) : null;
		const effectiveEnd =
			latestEntry && projectEnd && latestEntry > projectEnd
				? addDays(latestEntry, 14)
				: projectEnd
				|| (latestEntry && latestEntry > today ? addDays(latestEntry, 14) : addDays(today, 28));

		const totalDays = Math.max(14, daysBetween(effectiveStart, effectiveEnd) + 14);
		const totalWeeks = Math.ceil(totalDays / 7);
		const daysSinceStart = daysBetween(effectiveStart, today);

		return {
			people,
			effectiveStart,
			projectStart,
			projectEnd,
			durationWeeks,
			today,
			totalWeeks,
			daysSinceStart,
		};
	}, [atAGlanceSection, budgetsSection, timeEntriesSection, phaseRecord]);

	const {
		people,
		effectiveStart,
		projectStart,
		projectEnd,
		durationWeeks,
		today: todayDate,
		totalWeeks,
		daysSinceStart,
	} = scheduleData;

	const phaseStatusLower = String(phaseRecord?.["BQE Status"] || "").trim().toLowerCase();
	// The on-hold period feature (editing it, showing it, and its budget blackout
	// effect) is available on both Active and On Hold projects — a PM can plan or
	// record a hold without the BQE Status field having to already say "On Hold".
	const isOnHoldEligible = phaseStatusLower === "active" || phaseStatusLower === "on hold";
	// Separately: is the project's status LITERALLY "On Hold" right now — used only
	// for the "dates aren't set" notice, which shouldn't fire for an ordinary Active
	// project that simply has no reason to have on-hold dates yet.
	const isCurrentlyOnHold = phaseStatusLower === "on hold";
	// Sourced only from onHoldSection (the at_a_glance section, guaranteed loaded by
	// the time this editable UI renders) — no phaseRecord fallback. phaseRecord is a
	// point-in-time shell snapshot that never gets patched when on-hold data is
	// cleared, so falling back to it would make cleared dates reappear.
	const onHoldRawStart = onHoldSection?.start_date || "";
	const onHoldWindowEndRaw = onHoldSection?.end_date || "";
	const onHoldRawEndDate = onHoldSection?.estimated_end_date || onHoldWindowEndRaw || "";
	const onHoldParsedStart = parseLocalDate(onHoldRawStart);
	const onHoldParsedEnd = parseLocalDate(onHoldRawEndDate);
	const onHoldWindowEnd = parseLocalDate(onHoldWindowEndRaw);
	// Same rule as the main schedule — no stored duration, always calculated from
	// on-hold start/end.
	const onHoldDurationWeeks = onHoldParsedStart && onHoldWindowEnd
		? Math.round(daysBetween(onHoldParsedStart, onHoldWindowEnd) / 7)
		: null;

	// Budget is only excluded from the on-hold window when the phase is Active or On
	// Hold AND both on-hold start and end are set. Any other status (Completed,
	// Inactive, Canceled) disregards leftover on-hold dates entirely — otherwise a
	// stale window would keep blacking out budget projections indefinitely.
	const onHoldWindowActive = isOnHoldEligible && !!(onHoldParsedStart && onHoldWindowEnd);

	const visibleWeeks = view === "days" ? 2 : view === "weeks" ? 6 : totalWeeks;
	const maxScroll = Math.max(0, totalWeeks - visibleWeeks);

	useEffect(() => {
		setScroll((s) => Math.min(s, maxScroll));
	}, [maxScroll]);

	// Track whether a real data fetch has ever started for this project mount
	useEffect(() => {
		if (isLoading) loadingEverStarted.current = true;
	}, [isLoading]);

	// Center on today once, after the first real data load completes
	useEffect(() => {
		if (scrollInitialized.current || !loadingEverStarted.current || isLoading) return;
		scrollInitialized.current = true;
		const weeksView = 6;
		const maxS = Math.max(0, totalWeeks - weeksView);
		setScroll(Math.max(0, Math.min(maxS, daysSinceStart / 7 - weeksView / 2)));
	}, [isLoading, totalWeeks, daysSinceStart]);


	const pxPerDay = trackW / (visibleWeeks * 7);
	const pxPerWeek = pxPerDay * 7;
	const dayToPx = (day) => (day - scroll * 7) * pxPerDay;
	const todayPx = dayToPx(daysSinceStart);
	const startPx = projectStart ? dayToPx(daysBetween(effectiveStart, projectStart)) : null;
	const endPx = projectEnd ? dayToPx(daysBetween(effectiveStart, projectEnd) + 1) : null;

	// Tick marks
	const ticks = [];
	if (view === "days") {
		const startDay = Math.floor(scroll * 7);
		const endDay = Math.ceil((scroll + visibleWeeks) * 7);
		for (let d = startDay; d <= endDay; d++) {
			const pos = dayToPx(d);
			if (pos >= -5 && pos <= trackW + 5) {
				const date = addDays(effectiveStart, d);
				ticks.push({
					pos,
					label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
				});
			}
		}
	} else if (view === "weeks") {
		// Align tick labels to Mondays regardless of what day effectiveStart falls on
		const dow = effectiveStart.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
		const offsetToFirstMonday = (8 - dow) % 7; // days from effectiveStart to first Monday (0 if already Monday)

		const startDay = Math.floor(scroll * 7);
		const endDay = Math.ceil((scroll + visibleWeeks) * 7);
		const nStart = Math.max(0, Math.floor((startDay - offsetToFirstMonday) / 7));
		const nEnd = Math.ceil((endDay - offsetToFirstMonday) / 7);

		for (let n = nStart; n <= nEnd; n++) {
			const day = offsetToFirstMonday + n * 7;
			const pos = dayToPx(day);
			if (pos >= -20 && pos <= trackW + 20) {
				const date = addDays(effectiveStart, day);
				ticks.push({
					pos,
					label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
				});
			}
		}
	} else {
		let cur = new Date(effectiveStart.getFullYear(), effectiveStart.getMonth(), 1);
		const endDay = addDays(effectiveStart, (scroll + visibleWeeks) * 7);
		while (cur <= endDay) {
			const dayOff = daysBetween(effectiveStart, cur);
			const pos = dayToPx(dayOff);
			if (pos >= -60 && pos <= trackW + 60) {
				ticks.push({
					pos,
					label: cur.toLocaleDateString("en-US", { month: "short" }),
				});
			}
			cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
		}
	}

	// Distribute remaining budget hours evenly over remaining weeks (Monday-aligned)
	const budgetCellsFor = (person) => {
		if (!projectEnd || person.remaining === null || person.remaining <= 0)
			return [];

		const empInfo = empHoursMap.get(normalizeName(person.name));
		const contractedHoursPerDay = empInfo?.hoursPerDay ?? 8;

		// Align weeks to Mondays
		const startDow = effectiveStart.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
		const offsetToFirstMonday = (8 - startDow) % 7;
		const startMondayWeek = Math.max(0, Math.floor((daysSinceStart - offsetToFirstMonday) / 7));

		const personTimeOff = timeOffByName.get(person.name) || [];

		// Returns the fraction of the day this person is available (0 = fully off, 1 = fully on, 0.5 = half day).
		// Weekends are never passed here. Company holidays count as fully off.
		// BambooHR's per-day hours (entry.dates) are used to compute partial-day fractions
		// against the person's contracted hours per day (defaults to 8 if not set).
		const availFraction = (date) => {
			const t = date.getTime();
			const isoDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

			if (onHoldWindowActive && t >= onHoldParsedStart.getTime() && t <= onHoldWindowEnd.getTime()) return 0;

			if (holidays.some((h) => t >= h.start.getTime() && t <= h.end.getTime())) return 0;

			for (const period of personTimeOff) {
				if (t < period.start.getTime() || t > period.end.getTime()) continue;
				const hoursOff = parseFloat(period.dates?.[isoDate]);
				if (isNaN(hoursOff) || hoursOff <= 0) return contractedHoursPerDay / 8;
				return Math.max(0, (contractedHoursPerDay - hoursOff) / 8);
			}

			return contractedHoursPerDay / 8;
		};

		// Count available working days (Mon–Fri only) per future Monday-week, summing fractions for partial days.
		const weekData = [];
		for (let n = startMondayWeek; ; n++) {
			const dayOffset = offsetToFirstMonday + n * 7;
			if (dayOffset >= totalWeeks * 7) break;
			const weekStart = addDays(effectiveStart, dayOffset);
			if (weekStart >= projectEnd) break;
			let availDays = 0;
			for (let d = 0; d < 7; d++) {
				const day = addDays(weekStart, d);
				if (day >= projectEnd) break;
				if (day < todayDate) continue; // skip days already past
				const dow = day.getDay(); // 0 = Sun, 6 = Sat
				if (dow === 0 || dow === 6) continue; // skip weekends
				availDays += availFraction(day);
			}
			weekData.push({ mondayDayOffset: dayOffset, availDays });
		}

		const totalAvailDays = weekData.reduce((s, w) => s + w.availDays, 0);
		if (totalAvailDays === 0) return [];

		const availWeeks = weekData.filter((w) => w.availDays > 0);

		// If spreading would give less than 1h per week on average, show the full
		// remaining as a single chip in the first available week instead.
		if (person.remaining / availWeeks.length < 0.1) {
			const hours = Math.max(0.1, Math.round(person.remaining * 10) / 10);
			return [{ mondayDayOffset: availWeeks[0].mondayDayOffset, hours }];
		}

		// Cumulative rounding: round the running total at each week and take the
		// difference. This ensures the bars always sum to person.remaining (±0.05)
		// rather than accumulating independent rounding errors across many weeks.
		const hoursPerDay = person.remaining / totalAvailDays;
		let exactRunning = 0;
		let roundedRunning = 0;
		const cells = [];
		for (const w of availWeeks) {
			exactRunning += hoursPerDay * w.availDays;
			const weekHours = Math.round((exactRunning - roundedRunning) * 10) / 10;
			if (weekHours > 0) {
				cells.push({ mondayDayOffset: w.mondayDayOffset, hours: weekHours });
				roundedRunning += weekHours;
			}
		}

		// Fallback: if all weeks still rounded to zero, single chip (min 0.1h).
		if (cells.length === 0) {
			const hours = Math.max(0.1, Math.round(person.remaining * 10) / 10);
			return [{ mondayDayOffset: availWeeks[0].mondayDayOffset, hours }];
		}

		return cells;
	};

	if (isLoading) {
		const skeletonRows = [
			{ nameW: "68%", bars: 2, chips: 1, statsCount: 3, light: true },
			{ nameW: "55%", bars: 3, chips: 2, statsCount: 3, light: false },
			{ nameW: "72%", bars: 1, chips: 1, statsCount: 1, light: true },
			{ nameW: "60%", bars: 2, chips: 1, statsCount: 3, light: false },
		];
		return (
			<div className="projectViewerTabPanel">
				{/* Schedule info bar skeleton */}
				<div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 12 }}>
					{[{ labelW: 52, valueW: 88 }, { labelW: 58, valueW: 72 }, { labelW: 58, valueW: 88 }].map((f, i) => (
						<div key={i} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
							<div className="projectViewerSkeleton" style={{ width: f.labelW, height: 11, borderRadius: 4 }} />
							<div className="projectViewerSkeleton" style={{ width: f.valueW, height: 14, borderRadius: 4 }} />
						</div>
					))}
					<div className="projectViewerSkeleton" style={{ width: 70, height: 30, borderRadius: 4 }} />
				</div>

				<div
					className="projectViewerSkeletonCard"
					style={{ background: "#fff", border: "1px solid #e3e6eb", borderRadius: 8 }}
				>
					{/* Controls header */}
					<div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, padding: "10px 16px", borderBottom: "1px solid #eef0f3" }}>
						<div className="projectViewerSkeleton" style={{ width: 88, height: 26, borderRadius: 4 }} />
						<div style={{ display: "flex", gap: 14 }}>
							{[50, 58, 66].map((w, i) => (
								<div key={i} className="projectViewerSkeleton" style={{ width: w, height: 13, borderRadius: 4 }} />
							))}
						</div>
					</div>

					{/* Today marker strip */}
					<div style={{ height: 18, borderBottom: "1px solid #eef0f3" }} />

					{/* Person rows */}
					{skeletonRows.map((row, i) => (
						<div
							key={i}
							style={{
								display: "flex",
								alignItems: "center",
								height: ROW_H,
								background: row.light ? "#f4f6f9" : "#fff",
								borderTop: "1px solid #eef0f3",
							}}
						>
							{/* Name */}
							<div style={{ width: LEFT_W, flexShrink: 0, padding: "0 16px" }}>
								<div className="projectViewerSkeletonStack" style={{ gap: 7 }}>
									<div className="projectViewerSkeleton" style={{ width: row.nameW, height: 13 }} />
									{row.statsCount === 1 && (
										<div className="projectViewerSkeleton" style={{ width: "42%", height: 10 }} />
									)}
								</div>
							</div>

							{/* Track */}
							<div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, padding: "0 16px", height: "100%", overflow: "hidden" }}>
								{Array.from({ length: row.bars }).map((_, j) => (
									<div
										key={j}
										className="projectViewerSkeleton"
										style={{ flex: j === 0 ? 2 : 1, height: 18, borderRadius: 6, minWidth: 0 }}
									/>
								))}
								{Array.from({ length: row.chips }).map((_, j) => (
									<div
										key={`chip-${j}`}
										className="projectViewerSkeleton"
										style={{ width: 26, height: 30, borderRadius: 6, flexShrink: 0 }}
									/>
								))}
							</div>

							{/* Right stats */}
							<div style={{ width: RIGHT_W, flexShrink: 0, padding: "0 16px", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
								{Array.from({ length: row.statsCount }).map((_, j) => (
									<div
										key={j}
										className="projectViewerSkeleton"
										style={{ width: j === 0 ? "65%" : j === 1 ? "55%" : "75%", height: 11 }}
									/>
								))}
							</div>
						</div>
					))}

					{/* X-axis ticks */}
					<div style={{ display: "flex", borderTop: "1px solid #eef0f3", height: 18, alignItems: "center" }}>
						<div style={{ width: LEFT_W, flexShrink: 0 }} />
						<div style={{ flex: 1, display: "flex", justifyContent: "space-around", padding: "0 8px" }}>
							{[38, 30, 38, 30, 38, 30].map((w, i) => (
								<div key={i} className="projectViewerSkeleton" style={{ width: w, height: 10 }} />
							))}
						</div>
						<div style={{ width: RIGHT_W, flexShrink: 0 }} />
					</div>

					{/* Scrollbar */}
					<div style={{ borderTop: "1px solid #eef0f3", padding: "12px 16px" }}>
						<div className="projectViewerSkeleton" style={{ width: "100%", height: 16, borderRadius: 8 }} />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="projectViewerTabPanel">
			{(() => {
				const dangerNotices = [
					people.length === 0 && "No schedule data available for this project — add a budget or time entries to populate it.",
					!isLoading && people.length > 0 && !budgetsSection?.budget_count && "No budget set",
					!projectStart && "No start date set — timeline is anchored to the earliest time entry.",
					!projectEnd && "No end date set — remaining budget hours cannot be distributed over time.",
					!isLoading && projectEnd && projectEnd < todayDate && "Project end date has passed.",
				].filter(Boolean);
				const warningNotices = [
					isCurrentlyOnHold && !onHoldWindowActive && "Project is on hold, but on-hold start and end date aren't both set — budget projections are running as if the project were active.",
				].filter(Boolean);

				const renderNoticeBox = (notices, variant) => notices.length === 0 ? null : (
					<div className={`projectViewerMissingInfoNotice ${variant}`}>
						<InfoOutlinedIcon fontSize="inherit" />
						<div>
							{notices.length === 1 ? notices[0] : (
								<ul style={{ margin: 0, paddingLeft: 16 }}>
									{notices.map((n, i) => <li key={i}>{n}</li>)}
								</ul>
							)}
						</div>
					</div>
				);

				return (
					<>
						{renderNoticeBox(dangerNotices, "danger")}
						{renderNoticeBox(warningNotices, "warning")}
					</>
				);
			})()}
			{/* Top bar: project schedule info with inline editing — on-hold fields join the
			    same row and the same edit dialog when the phase is on hold. */}
			{(() => {
				const durInfo = atAGlanceSection?.estimated_project_duration || {};
				const rawStart = durInfo.start_date || phaseRecord?.["CF-Start Date"] || "";
				const rawEndDate = durInfo.estimated_end_date || durInfo.end_date || phaseRecord?.["CF-End Date"] || "";

				return (
					<ScheduleFieldsEditor
						fieldLabels={{ start: "Start Date", duration: "Duration", end: "End Date" }}
						rawStart={rawStart}
						rawEndDate={rawEndDate}
						parsedStart={projectStart}
						parsedEnd={projectEnd}
						durationWeeks={durationWeeks}
						todayDate={todayDate}
						highlightPastEnd
						dialogTitle="Edit Schedule"
						onSave={onSaveSchedule}
						onHold={isOnHoldEligible ? {
							fieldLabels: { start: "On Hold Start Date", duration: "On Hold Duration", end: "On Hold End Date" },
							rawStart: onHoldRawStart,
							rawEndDate: onHoldRawEndDate,
							parsedStart: onHoldParsedStart,
							parsedEnd: onHoldParsedEnd,
							durationWeeks: onHoldDurationWeeks,
							onSave: onSaveOnHoldSchedule,
						} : null}
					/>
				);
			})()}

			{/* Chart */}
			<div
				ref={chartRef}
				style={{
					background: "#fff",
					border: "1px solid #e3e6eb",
					borderRadius: 8,
					overflow: "hidden",
				}}
			>
				{/* Controls header */}
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						alignItems: "center",
						gap: 10,
						padding: "10px 16px",
						borderBottom: "1px solid #eef0f3",
					}}
				>
					{(() => {
						const todayOffRange = daysSinceStart < 0 || daysSinceStart > totalWeeks * 7;
						return (
							<button
								onClick={() => setScroll(Math.max(0, Math.min(maxScroll, daysSinceStart / 7 - visibleWeeks / 2)))}
								disabled={todayOffRange}
								style={{
									fontSize: 12,
									padding: "3px 10px",
									border: "1px solid #b6bcc7",
									borderRadius: 4,
									background: "#f4f6f9",
									cursor: todayOffRange ? "default" : "pointer",
									whiteSpace: "nowrap",
									color: "#3a4051",
									opacity: todayOffRange ? 0.4 : 1,
								}}
							>
								Jump to Today
							</button>
						);
					})()}
					<div style={{ display: "flex", gap: 10, fontSize: 13, whiteSpace: "nowrap" }}>
						{[
							{ value: "days", label: "Days" },
							{ value: "weeks", label: "Weeks" },
							{ value: "months", label: "Months" },
						].map(({ value, label }) => (
							<label
								key={value}
								style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
							>
								<input
									type="radio"
									name="scheduleView"
									value={value}
									checked={view === value}
									onChange={() => setView(value)}
									style={{ margin: 0, verticalAlign: "middle" }}
								/>
								{label}
							</label>
						))}
					</div>
				</div>

				{/* Today marker row */}
				<div
					style={{
						display: "flex",
						alignItems: "flex-end",
						height: 18,
						position: "relative",
					}}
				>
					<div style={{ width: LEFT_W, flexShrink: 0 }} />
					<div
						ref={trackRef}
						style={{ flex: 1, position: "relative", height: "100%", overflow: "hidden" }}
					>
						{startPx !== null && startPx >= 0 && startPx <= trackW && (
							<>
								<div
									style={{
										position: "absolute",
										left: startPx + 4,
										bottom: 0,
										padding: "1px 6px",
										background: "#dcfce7",
										border: "1px solid #4ea861",
										borderRadius: 4,
										fontSize: 11,
										fontWeight: 600,
										color: "#14532d",
										zIndex: 3,
										whiteSpace: "nowrap",
									}}
								>
									Start
								</div>
								<div
									style={{
										position: "absolute",
										left: startPx,
										bottom: -6,
										transform: "translateX(-50%)",
										width: 0,
										height: 0,
										borderLeft: "6px solid transparent",
										borderRight: "6px solid transparent",
										borderTop: "6px solid #4ea861",
										zIndex: 3,
									}}
								/>
							</>
						)}
						{endPx !== null && endPx >= 0 && endPx <= trackW && (
							<>
								<div
									style={{
										position: "absolute",
										left: endPx,
										bottom: 0,
										transform: "translateX(-100%)",
										marginLeft: -4,
										padding: "1px 6px",
										background: "#fee2e2",
										border: "1px solid #c62828",
										borderRadius: 4,
										fontSize: 11,
										fontWeight: 600,
										color: "#7f1d1d",
										zIndex: 3,
										whiteSpace: "nowrap",
									}}
								>
									End
								</div>
								<div
									style={{
										position: "absolute",
										left: endPx,
										bottom: -6,
										transform: "translateX(-50%)",
										width: 0,
										height: 0,
										borderLeft: "6px solid transparent",
										borderRight: "6px solid transparent",
										borderTop: "6px solid #c62828",
										zIndex: 3,
									}}
								/>
							</>
						)}
						{todayPx >= 0 && todayPx <= trackW && (
							<>
								<div
									style={{
										position: "absolute",
										left: todayPx,
										bottom: 0,
										transform: "translateX(-50%)",
										padding: "1px 6px",
										background: "#e8eaee",
										border: "1px solid #b6bcc7",
										borderRadius: 4,
										fontSize: 11,
										fontWeight: 600,
										color: "#3a4051",
										zIndex: 3,
										whiteSpace: "nowrap",
									}}
								>
									Today
								</div>
								<div
									style={{
										position: "absolute",
										left: todayPx,
										bottom: -6,
										transform: "translateX(-50%)",
										width: 0,
										height: 0,
										borderLeft: "6px solid transparent",
										borderRight: "6px solid transparent",
										borderTop: "6px solid #b6bcc7",
										zIndex: 3,
									}}
								/>
							</>
						)}
					</div>
					<div style={{ width: RIGHT_W, flexShrink: 0 }} />
				</div>

				{/* Grid + rows */}
				<div style={{ position: "relative" }}>
					{/* Grid line overlay */}
					<div
						style={{
							position: "absolute",
							top: 0,
							left: LEFT_W,
							right: RIGHT_W,
							bottom: 0,
							pointerEvents: "none",
							zIndex: 0,
						}}
					>
						{ticks.map((t, i) => (
							<div
								key={i}
								style={{
									position: "absolute",
									left: t.pos,
									top: 0,
									bottom: 0,
									borderLeft: "1px dashed #d8dce3",
								}}
							/>
						))}
						{startPx !== null && startPx >= 0 && startPx <= trackW && (
							<div
								style={{
									position: "absolute",
									left: startPx,
									top: 0,
									bottom: 0,
									borderLeft: "2px solid #4ea861",
									opacity: 0.5,
								}}
							/>
						)}
						{endPx !== null && endPx >= 0 && endPx <= trackW && (
							<div
								style={{
									position: "absolute",
									left: endPx,
									top: 0,
									bottom: 0,
									borderLeft: "2px solid #c62828",
									opacity: 0.5,
								}}
							/>
						)}
						{todayPx >= 0 && todayPx <= trackW && (
							<div
								style={{
									position: "absolute",
									left: todayPx,
									top: 0,
									bottom: 0,
									borderLeft: "2px solid #3a4051",
									opacity: 0.45,
								}}
							/>
						)}
					</div>

					{/* Person rows */}
					{people.map((person, idx) => {
						const isOver = person.remaining !== null && person.remaining < 0;
						const cells = budgetCellsFor(person);
						const visibleEntries = person.entries.filter((e) => {
							const px = dayToPx(daysBetween(effectiveStart, e.date));
							return px >= -20 && px <= trackW + 20;
						});

						return (
							<div
								key={person.name}
								style={{
									display: "flex",
									alignItems: "stretch",
									height: ROW_H,
									background: idx % 2 === 0 ? "#f4f6f9" : "#fff",
									borderTop: "1px solid #eef0f3",
								}}
							>
								{/* Name */}
								<div
									style={{
										width: LEFT_W,
										flexShrink: 0,
										display: "flex",
										alignItems: "center",
										padding: "0 16px",
									}}
								>
									<div>
										<div
											style={{
												fontSize: 13,
												fontWeight: 600,
												color: !person.inBudget ? "#ca8a04" : "#1a2540",
												lineHeight: 1.3,
											}}
										>
											{person.name}
										</div>
										{!person.inBudget && (
											<div style={{ fontSize: 10, color: "#ca8a04", marginTop: 1 }}>
												Not in budget
											</div>
										)}
										{(() => {
											const info = empHoursMap.get(normalizeName(person.name));
											if (!info?.employmentType && info?.hoursPerWeek == null) return null;
											const parts = [
												info.employmentType === "full_time" ? "Full Time" : info.employmentType === "part_time" ? "Part Time" : null,
												info.hoursPerWeek != null ? `${info.hoursPerWeek}h/wk` : null,
											].filter(Boolean);
											if (parts.length === 0) return null;
											return (
												<div style={{ fontSize: 10, color: "#9ea8b6", marginTop: 1 }}>
													{parts.join(" · ")}
												</div>
											);
										})()}
									</div>
								</div>

								{/* Track */}
								<div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
									{mergeConsecutivePeriods(timeOffByName.get(person.name) || []).map((period, i) => (
										<TimeOffBar
											key={i}
											startDate={period.start}
											endDate={period.end}
											effectiveStart={effectiveStart}
											dayToPx={dayToPx}
											pxPerDay={pxPerDay}
											trackW={trackW}
										/>
									))}
									{(() => {
										const CHIP_W = 26;
										const CHIP_GAP = 2;
										// Group by day to compute per-chip horizontal offsets
										const dayMap = new Map();
										visibleEntries.forEach((entry) => {
											const key = daysBetween(effectiveStart, entry.date);
											if (!dayMap.has(key)) dayMap.set(key, []);
											dayMap.get(key).push(entry);
										});
										return visibleEntries.map((entry, i) => {
											const dayKey = daysBetween(effectiveStart, entry.date);
											const group = dayMap.get(dayKey);
											const idx = group.indexOf(entry);
											const n = group.length;
											const centerPx = dayToPx(dayKey) + pxPerDay / 2;
											// Spread chips symmetrically around the day cell's center
											const offset = (idx - (n - 1) / 2) * (CHIP_W + CHIP_GAP);
											return (
												<TimeEntryChip
													key={i}
													entry={entry}
													left={centerPx - CHIP_W / 2 + offset}
													trackW={trackW}
												/>
											);
										});
									})()}
									{cells.map((c) => {
										const left = dayToPx(c.mondayDayOffset);
										if (left + pxPerWeek < 0 || left > trackW) return null;
										const widthPx = endPx !== null
											? Math.max(0, Math.min(pxPerWeek, endPx - left))
											: pxPerWeek;
										return (
											<BudgetBar
												key={c.mondayDayOffset}
												hours={c.hours}
												leftPx={left}
												widthPx={widthPx}
											/>
										);
									})}
								</div>

								{/* Right summary */}
								<div
									style={{
										width: RIGHT_W,
										flexShrink: 0,
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
										alignItems: "flex-end",
										padding: "0 16px",
										fontSize: 12,
										lineHeight: 1.7,
										color: "#3a4051",
									}}
								>
									<div style={!person.inBudget ? { color: "#ca8a04" } : undefined}>
										Spent — <strong>{person.spent}</strong>h
									</div>
									{person.budget !== null && (
										<>
											<div style={{ color: isOver ? "#c62828" : person.remaining > 0 ? "#2e7d32" : "#3a4051" }}>
												Remaining — <strong>{person.remaining}</strong>h
											</div>
											<div>
												Budget — <strong>{person.budget}</strong>h
											</div>
										</>
									)}
								</div>
							</div>
						);
					})}

					{/* Holiday overlay — after person rows so it paints on top and hover works,
					     capped to person-row height so it doesn't extend over the totals footer */}
					<div
						style={{
							position: "absolute",
							top: 0,
							left: LEFT_W,
							right: RIGHT_W,
							height: people.length * ROW_H,
							pointerEvents: "none",
						}}
					>
						{mergeConsecutiveHolidays(holidays).map((holiday, hi) => {
							const startDay = daysBetween(effectiveStart, holiday.start);
							const endDay = daysBetween(effectiveStart, holiday.end);
							const leftPx = dayToPx(startDay);
							const rightPx = dayToPx(endDay) + pxPerDay;
							if (rightPx < 0 || leftPx > trackW) return null;
							const cl = Math.max(0, leftPx);
							const cw = Math.min(trackW, rightPx) - cl;
							if (cw <= 0) return null;
							const nameStr = holiday.names.join(" & ");
							const isSingleDay = holiday.start.getTime() === holiday.end.getTime();
							const dateStr = isSingleDay
								? fmtDate(holiday.start)
								: `${fmtDate(holiday.start)} – ${fmtDate(holiday.end)}`;
							return (
								<Tooltip key={hi} arrow placement="top" title={`${nameStr} — ${dateStr}`}>
									<div
										style={{
											position: "absolute",
											left: cl,
											width: cw,
											top: 0,
											bottom: 0,
											background: "rgba(20, 184, 166, 0.15)",
											borderLeft: leftPx >= 0 ? "1.5px solid rgba(13, 148, 136, 0.5)" : "none",
											borderRight: rightPx <= trackW ? "1.5px solid rgba(13, 148, 136, 0.5)" : "none",
											pointerEvents: "auto",
											cursor: "default",
											boxSizing: "border-box",
										}}
									/>
								</Tooltip>
							);
						})}
					</div>

					{/* On-hold band — spans the whole schedule (not per-person) so it's clear why
					     no projected budget bars appear during this window. */}
					{onHoldWindowActive && (
						<div
							style={{
								position: "absolute",
								top: 0,
								left: LEFT_W,
								right: RIGHT_W,
								height: people.length * ROW_H,
								pointerEvents: "none",
							}}
						>
							{(() => {
								const startDay = daysBetween(effectiveStart, onHoldParsedStart);
								const endDay = daysBetween(effectiveStart, onHoldWindowEnd);
								const leftPx = dayToPx(startDay);
								const rightPx = dayToPx(endDay) + pxPerDay;
								if (rightPx < 0 || leftPx > trackW) return null;
								const cl = Math.max(0, leftPx);
								const cw = Math.min(trackW, rightPx) - cl;
								if (cw <= 0) return null;
								const dateStr = `${fmtDate(onHoldParsedStart)} – ${fmtDate(onHoldWindowEnd)}`;
								return (
									<Tooltip arrow placement="top" title={`On Hold — ${dateStr}`}>
										<div
											style={{
												position: "absolute",
												left: cl,
												width: cw,
												top: 0,
												bottom: 0,
												background: "rgba(202, 138, 4, 0.10)",
												borderLeft: leftPx >= 0 ? "1.5px solid rgba(202, 138, 4, 0.45)" : "none",
												borderRight: rightPx <= trackW ? "1.5px solid rgba(202, 138, 4, 0.45)" : "none",
												pointerEvents: "auto",
												cursor: "default",
												boxSizing: "border-box",
											}}
										/>
									</Tooltip>
								);
							})()}
						</div>
					)}

				{/* Totals footer row */}
					{(() => {
						const totalBudget = Math.round(people.reduce((sum, p) => p.budget !== null ? sum + p.budget : sum, 0) * 100) / 100;
						const hasBudget = people.some((p) => p.budget !== null);
						const totalSpent = Math.round(people.reduce((sum, p) => sum + p.spent, 0) * 100) / 100;
						const totalRemaining = hasBudget ? Math.round((totalBudget - totalSpent) * 100) / 100 : null;
						const totalIsOver = totalRemaining !== null && totalRemaining < 0;
						return (
							<div
								style={{
									display: "flex",
									alignItems: "stretch",
									borderTop: "3px solid #b6bcc7",
									background: "#f4f6f9",
								}}
							>
								<div
									style={{
										width: LEFT_W,
										flexShrink: 0,
										display: "flex",
										alignItems: "center",
										padding: "0 16px",
										fontSize: 12,
										fontWeight: 700,
										color: "#3a4051",
									}}
								>
									Total
								</div>
								<div style={{ flex: 1 }} />
								<div
									style={{
										width: RIGHT_W,
										flexShrink: 0,
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
										alignItems: "flex-end",
										padding: "8px 16px",
										fontSize: 12,
										lineHeight: 1.7,
										color: "#3a4051",
									}}
								>
									{hasBudget && (
										<div>
											Budget — <strong>{totalBudget}</strong>h
										</div>
									)}
									<div>
										Spent — <strong>{totalSpent}</strong>h
									</div>
									{hasBudget && (
										<div style={{ color: totalIsOver ? "#c62828" : totalRemaining > 0 ? "#2e7d32" : "#3a4051" }}>
											Remaining — <strong>{totalRemaining}</strong>h
										</div>
									)}
								</div>
							</div>
						);
					})()}

					{/* X-axis tick labels */}
					<div
						style={{
							display: "flex",
							borderTop: "1px solid #eef0f3",
							height: 18,
							alignItems: "center",
						}}
					>
						<div style={{ width: LEFT_W, flexShrink: 0 }} />
						<div
							style={{ flex: 1, position: "relative", height: "100%", overflow: "hidden" }}
						>
							{ticks.map((t, i) => (
								<span
									key={i}
									style={{
										position: "absolute",
										left: t.pos + 4,
										top: "50%",
										transform: "translateY(-50%)",
										fontSize: 11,
										color: "#7a8190",
										whiteSpace: "nowrap",
									}}
								>
									{t.label}
								</span>
							))}
						</div>
						<div style={{ width: RIGHT_W, flexShrink: 0 }} />
					</div>
				</div>

				{/* Footer: scrollbar */}
				<div
					style={{
						borderTop: "1px solid #eef0f3",
						padding: "12px 16px",
					}}
				>
					<TimelineScrollbar
						value={scroll}
						max={maxScroll}
						thumbFraction={Math.min(1, visibleWeeks / Math.max(1, totalWeeks))}
						onChange={setScroll}
						disabled={maxScroll <= 0}
					/>
				</div>
			</div>

			{/* Legend */}
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					gap: "6px 16px",
					marginTop: 10,
					alignItems: "center",
				}}
			>
				<LegendSwatch color="#b9f0c0" border="#4ea861" label="Time entries (billable)" />
				<LegendSwatch color="#fee2e2" border="#ef4444" label="Time entries (non-billable)" />
				{!!projectEnd && (
					<LegendSwatch
						color="#bcd8ff"
						border="#5b8dd6"
						label="Remaining budget (planned)"
						info="The remaining budget hours are spread evenly across working days (Monday–Friday) between now and the project end date. Days when someone is off or a company holiday falls are skipped, and partial days off reduce that day's share. The total always adds up correctly."
					/>
				)}
				<LegendSwatch color="rgba(124,58,237,0.08)" border="rgba(124,58,237,0.40)" label="Time off" />
				<LegendSwatch color="rgba(20,184,166,0.15)" border="rgba(13,148,136,0.5)" label="Company holiday" />
				{onHoldWindowActive && (
					<LegendSwatch
						color="rgba(202,138,4,0.10)"
						border="rgba(202,138,4,0.45)"
						label="On hold period"
						info="Budget hours aren't projected onto working days that fall inside the on-hold window."
					/>
				)}
			</div>
		</div>
	);
}
